import React, { createContext, useContext, useState, useCallback } from 'react';
import { STATION_CONFIGS } from '../../../config/stationConfigs';

const PiersContext = createContext();

export const usePiers = () => {
  const context = useContext(PiersContext);
  if (!context) {
    throw new Error('usePiers must be used within a PiersProvider');
  }
  return context;
};

export const PiersProvider = ({ children, station }) => {
  const stationConfig = STATION_CONFIGS[station];

  const [flightsByDate, setFlightsByDate] = useState({});
  const [beltAssignmentsByDate, setBeltAssignmentsByDate] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [configuration, setConfiguration] = useState({
    activeBelts: stationConfig.activeBelts,
    conflictWindow: stationConfig.conflictWindow,
    maxFlightsPerHour: stationConfig.maxFlightsPerHour,
    beltPairs: stationConfig.beltPairs,
    beltSchedule: { ...stationConfig.beltSchedule },
    muAssignmentTimeOverride: { ...stationConfig.muAssignmentTimeOverride },
    makeupUnits: { ...stationConfig.makeupUnits },
    pierSupport: { ...stationConfig.pierSupport },
    beltNames: { ...stationConfig.beltNames },
    exportGroups: stationConfig.exportGroups,
    destinationBeltGroups: stationConfig.destinationBeltGroups || null
  });
  const [importedFileName, setImportedFileName] = useState('');

  const updateConfiguration = useCallback((updates) => {
    setConfiguration(prev => ({ ...prev, ...updates }));
  }, []);

  // Get all belts paired with a given belt (from beltPairs config)
  const getPairedBelts = useCallback((beltId) => {
    const partners = new Set();
    configuration.beltPairs.forEach(([a, b]) => {
      if (a === beltId) partners.add(b);
      if (b === beltId) partners.add(a);
    });
    return Array.from(partners);
  }, [configuration.beltPairs]);

  const importFlights = useCallback((flightDataByDate, fileName, assignmentsByDate = {}) => {
    setFlightsByDate(flightDataByDate);
    setImportedFileName(fileName);
    setBeltAssignmentsByDate(assignmentsByDate);

    // Select the first date if available
    const dates = Object.keys(flightDataByDate).sort();
    if (dates.length > 0) {
      setSelectedDate(dates[0]);
    }
  }, []);

  const updateBeltAssignments = useCallback((assignments) => {
    if (!selectedDate) return;

    setBeltAssignmentsByDate(prev => ({
      ...prev,
      [selectedDate]: assignments
    }));
  }, [selectedDate]);

  const setAllBeltAssignments = useCallback((assignmentsByDate) => {
    setBeltAssignmentsByDate(assignmentsByDate);
  }, []);

  // flightKey is { flightNum, depTime } — flight numbers alone may not be
  // unique within a day, so identity needs both fields.
  const moveFlight = useCallback((flightKey, fromBelt, toBelt) => {
    if (!selectedDate) return;

    const matches = (f) => f.flightNum === flightKey.flightNum && f.depTime === flightKey.depTime;

    setBeltAssignmentsByDate(prev => {
      const dateAssignments = prev[selectedDate] || {};
      const updated = { ...dateAssignments };
      const flights = flightsByDate[selectedDate] || [];

      // Remove from old belt
      if (fromBelt && updated[fromBelt]) {
        updated[fromBelt] = updated[fromBelt].filter(f => !matches(f));
      }

      // Add to new belt
      if (toBelt) {
        if (!updated[toBelt]) updated[toBelt] = [];
        const flight = flights.find(matches);
        if (flight && !updated[toBelt].some(matches)) {
          updated[toBelt] = [...updated[toBelt], flight];
          // Sort by departure time
          updated[toBelt].sort((a, b) => a.depTime.localeCompare(b.depTime));
        }
      }

      return {
        ...prev,
        [selectedDate]: updated
      };
    });
  }, [selectedDate, flightsByDate]);

  // Get current date's data
  const flights = selectedDate ? (flightsByDate[selectedDate] || []) : [];
  const beltAssignments = selectedDate ? (beltAssignmentsByDate[selectedDate] || {}) : {};
  const availableDates = Object.keys(flightsByDate).sort();

  const value = {
    flights,
    flightsByDate,
    beltAssignments,
    configuration,
    importedFileName,
    selectedDate,
    availableDates,
    station,
    stationConfig,
    importFlights,
    updateConfiguration,
    updateBeltAssignments,
    setAllBeltAssignments,
    moveFlight,
    setSelectedDate,
    getPairedBelts
  };

  return (
    <PiersContext.Provider value={value}>
      {children}
    </PiersContext.Provider>
  );
};
